import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Gamepad2, Smartphone, BookOpen, ExternalLink, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fetchDevices, fetchGuides, triggerSync, fetchSyncStatus } from "@/lib/api";
import { CHIPSET_TIERS, COOLING } from "@/data/static";

const lines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const commas = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);

const EMPTY_DEVICE = {
  slug: "", brand: "", model: "", name: "", chipset: "", chipset_tier: CHIPSET_TIERS[0], display: "",
  battery: "", ram_storage: "", cooling_notes: "", cooling_rating: "Good", fps_tier: 90, thermal_label: "",
  gyro_label: "", pta_status: "", image: "", price_min: 0, price_max: 0, popularity: 50,
  games: { pubg: "", cod: "", genshin: "", freefire: "" },
  pros: [], cons: [], conditions: ["Used Kit"], buying_checklist: [], related: [], used_market_notes: "",
  updated_date: new Date().toISOString().slice(0, 10),
};

const EMPTY_GUIDE = {
  slug: "", title: "", excerpt: "", cover_image: "", author: "The Kit Guide Desk",
  published_date: new Date().toISOString().slice(0, 10), updated_date: new Date().toISOString().slice(0, 10),
  read_minutes: 5, tags: [], risk_disclaimer: false, toc: [], body: "", faqs: [], related_devices: [], related_categories: [],
};

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("devices");
  const [devices, setDevices] = useState([]);
  const [guides, setGuides] = useState([]);
  const [editing, setEditing] = useState(null); // {type, data, isNew}
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    fetchDevices().then(setDevices).catch(() => {});
    fetchGuides().then(setGuides).catch(() => {});
  };
  const loadSyncStatus = () => fetchSyncStatus().then(setSyncStatus).catch(() => {});
  useEffect(() => { if (user) { load(); loadSyncStatus(); } }, [user]);

  useEffect(() => {
    if (syncStatus?.status !== "running") return;
    const t = setInterval(loadSyncStatus, 5000);
    return () => clearInterval(t);
  }, [syncStatus]);

  const runSync = async () => {
    setSyncing(true);
    try {
      await triggerSync();
      toast.success("Live data sync started - this can take a few minutes");
      loadSyncStatus();
    } catch (e) {
      toast.error("Could not start sync");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-charcoal text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const delDevice = async (slug) => {
    if (!window.confirm(`Delete device ${slug}?`)) return;
    await api.delete(`/devices/${slug}`); toast.success("Device deleted"); load();
  };
  const delGuide = async (slug) => {
    if (!window.confirm(`Delete guide ${slug}?`)) return;
    await api.delete(`/guides/${slug}`); toast.success("Guide deleted"); load();
  };

  return (
    <div className="min-h-screen bg-charcoal">
      <header className="sticky top-0 z-40 border-b border-border bg-charcoal/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <Gamepad2 className="h-5 w-5 text-neon" />
            <span className="font-display text-sm font-black tracking-wider text-white">CMS<span className="text-neon">·</span>ADMIN</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-1 text-xs text-slate-400 hover:text-white"><ExternalLink className="h-3.5 w-3.5" /> View site</Link>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-1 rounded-lg border border-border bg-graphite px-3 py-1.5 text-xs text-slate-300 hover:border-dangerr/50 hover:text-dangerr"><LogOut className="h-3.5 w-3.5" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex items-center gap-2">
          <TabBtn active={tab === "devices"} onClick={() => setTab("devices")} icon={Smartphone} label={`Devices (${devices.length})`} testid="tab-devices" />
          <TabBtn active={tab === "guides"} onClick={() => setTab("guides")} icon={BookOpen} label={`Guides (${guides.length})`} testid="tab-guides" />
        </div>

        {tab === "devices" && (
          <>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div data-testid="sync-status" className="flex items-center gap-2 text-xs text-slate-400">
                {syncStatus?.status === "running" && <span className="text-cyber">Syncing live data…</span>}
                {syncStatus?.status === "success" && (
                  <span>Last sync: {syncStatus.gaming_phones_found} gaming phones from {syncStatus.scraped_total} scraped ({syncStatus.benchmark_verified_count} verified) · {syncStatus.finished_at?.slice(0, 16).replace("T", " ")}</span>
                )}
                {syncStatus?.status === "error" && <span className="text-dangerr">Last sync failed: {syncStatus.error}</span>}
              </div>
              <div className="flex gap-2">
                <button data-testid="sync-priceoye-btn" onClick={runSync} disabled={syncing || syncStatus?.status === "running"} className="flex items-center gap-2 rounded-lg border border-cyber/40 bg-cyber/10 px-4 py-2 text-sm font-heading font-bold text-cyber disabled:opacity-50">
                  <RefreshCw className={`h-4 w-4 ${syncStatus?.status === "running" ? "animate-spin" : ""}`} /> Sync Live Data
                </button>
                <button data-testid="add-device" onClick={() => setEditing({ type: "device", data: { ...EMPTY_DEVICE }, isNew: true })} className="flex items-center gap-2 rounded-lg bg-neon px-4 py-2 text-sm font-heading font-bold text-charcoal"><Plus className="h-4 w-4" /> Add Device</button>
              </div>
            </div>
            <div className="overflow-hidden rounded-xl border border-border">
              {devices.map((d) => (
                <div key={d.slug} data-testid={`admin-device-${d.slug}`} className="flex items-center gap-3 border-b border-border bg-slateSurface px-4 py-3 last:border-0">
                  <img src={d.image} alt="" className="h-10 w-10 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-white">{d.name}</div>
                    <div className="text-xs text-slate-500">{d.chipset} · PKR {d.price_min.toLocaleString()}+ · {d.fps_tier} FPS</div>
                  </div>
                  <button onClick={() => setEditing({ type: "device", data: JSON.parse(JSON.stringify(d)), isNew: false })} className="rounded p-2 text-slate-400 hover:text-cyber"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`del-device-${d.slug}`} onClick={() => delDevice(d.slug)} className="rounded p-2 text-slate-400 hover:text-dangerr"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "guides" && (
          <>
            <div className="mb-4 flex justify-end">
              <button data-testid="add-guide" onClick={() => setEditing({ type: "guide", data: { ...EMPTY_GUIDE }, isNew: true })} className="flex items-center gap-2 rounded-lg bg-neon px-4 py-2 text-sm font-heading font-bold text-charcoal"><Plus className="h-4 w-4" /> Add Guide</button>
            </div>
            <div className="overflow-hidden rounded-xl border border-border">
              {guides.map((g) => (
                <div key={g.slug} data-testid={`admin-guide-${g.slug}`} className="flex items-center gap-3 border-b border-border bg-slateSurface px-4 py-3 last:border-0">
                  <img src={g.cover_image} alt="" className="h-10 w-16 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-white">{g.title}</div>
                    <div className="text-xs text-slate-500">{g.updated_date} · {g.read_minutes} min</div>
                  </div>
                  <button onClick={() => api.get(`/guides/${g.slug}`).then((r) => setEditing({ type: "guide", data: r.data, isNew: false }))} className="rounded p-2 text-slate-400 hover:text-cyber"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`del-guide-${g.slug}`} onClick={() => delGuide(g.slug)} className="rounded p-2 text-slate-400 hover:text-dangerr"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {editing && <EditModal editing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const TabBtn = ({ active, onClick, icon: Icon, label, testid }) => (
  <button data-testid={testid} onClick={onClick} className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-heading font-semibold transition-colors ${active ? "bg-neon text-charcoal" : "border border-border bg-slateSurface text-slate-300"}`}>
    <Icon className="h-4 w-4" /> {label}
  </button>
);

function EditModal({ editing, onClose, onSaved }) {
  const isDevice = editing.type === "device";
  const [d, setD] = useState(editing.data);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (isDevice) {
        const payload = {
          ...d,
          fps_tier: Number(d.fps_tier), price_min: Number(d.price_min), price_max: Number(d.price_max), popularity: Number(d.popularity),
          pros: Array.isArray(d.pros) ? d.pros : lines(d.pros),
          cons: Array.isArray(d.cons) ? d.cons : lines(d.cons),
          conditions: Array.isArray(d.conditions) ? d.conditions : commas(d.conditions),
          buying_checklist: Array.isArray(d.buying_checklist) ? d.buying_checklist : lines(d.buying_checklist),
          related: Array.isArray(d.related) ? d.related : commas(d.related),
        };
        await api.post("/devices", payload);
      } else {
        const payload = {
          ...d,
          read_minutes: Number(d.read_minutes),
          tags: Array.isArray(d.tags) ? d.tags : commas(d.tags),
          toc: Array.isArray(d.toc) ? d.toc : lines(d.toc),
          related_devices: Array.isArray(d.related_devices) ? d.related_devices : commas(d.related_devices),
          related_categories: Array.isArray(d.related_categories) ? d.related_categories : commas(d.related_categories),
        };
        await api.post("/guides", payload);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail ? String(e.response.data.detail) : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-2xl rounded-2xl border border-border bg-slateSurface p-6">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-white">{editing.isNew ? "Add" : "Edit"} {isDevice ? "Device" : "Guide"}</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>

        <div className="space-y-4">
          {isDevice ? (
            <>
              <Row><F label="Slug (unique)" v={d.slug} onC={(v) => set("slug", v)} disabled={!editing.isNew} testid="f-slug" /><F label="Name" v={d.name} onC={(v) => set("name", v)} /></Row>
              <Row><F label="Brand" v={d.brand} onC={(v) => set("brand", v)} /><F label="Model" v={d.model} onC={(v) => set("model", v)} /></Row>
              <Row><F label="Chipset" v={d.chipset} onC={(v) => set("chipset", v)} /><Sel label="Chipset Tier" v={d.chipset_tier} onC={(v) => set("chipset_tier", v)} opts={CHIPSET_TIERS} /></Row>
              <Row><F label="Display" v={d.display} onC={(v) => set("display", v)} /><F label="Battery" v={d.battery} onC={(v) => set("battery", v)} /></Row>
              <Row><F label="RAM / Storage" v={d.ram_storage} onC={(v) => set("ram_storage", v)} /><Sel label="Cooling Rating" v={d.cooling_rating} onC={(v) => set("cooling_rating", v)} opts={COOLING} /></Row>
              <F label="Cooling notes" v={d.cooling_notes} onC={(v) => set("cooling_notes", v)} />
              <Row><F label="FPS tier" type="number" v={d.fps_tier} onC={(v) => set("fps_tier", v)} /><F label="Popularity" type="number" v={d.popularity} onC={(v) => set("popularity", v)} /></Row>
              <Row><F label="Price min (PKR)" type="number" v={d.price_min} onC={(v) => set("price_min", v)} /><F label="Price max (PKR)" type="number" v={d.price_max} onC={(v) => set("price_max", v)} /></Row>
              <F label="Thermal label" v={d.thermal_label} onC={(v) => set("thermal_label", v)} />
              <F label="Gyro label" v={d.gyro_label} onC={(v) => set("gyro_label", v)} />
              <F label="PTA status text" v={d.pta_status} onC={(v) => set("pta_status", v)} />
              <F label="Image URL" v={d.image} onC={(v) => set("image", v)} />
              <div className="rounded-lg border border-border bg-graphite p-3">
                <div className="mb-2 text-xs font-semibold text-slate-400">Gaming performance by title</div>
                {["pubg", "cod", "genshin", "freefire"].map((g) => (
                  <F key={g} label={g.toUpperCase()} v={d.games[g]} onC={(v) => set("games", { ...d.games, [g]: v })} />
                ))}
              </div>
              <TA label="Pros (one per line)" v={arr(d.pros)} onC={(v) => set("pros", v)} />
              <TA label="Cons (one per line)" v={arr(d.cons)} onC={(v) => set("cons", v)} />
              <TA label="Buying checklist (one per line)" v={arr(d.buying_checklist)} onC={(v) => set("buying_checklist", v)} />
              <F label="Conditions (comma separated)" v={arr(d.conditions, ", ")} onC={(v) => set("conditions", v)} />
              <F label="Related slugs (comma separated)" v={arr(d.related, ", ")} onC={(v) => set("related", v)} />
              <TA label="Used-market notes" v={d.used_market_notes} onC={(v) => set("used_market_notes", v)} />
            </>
          ) : (
            <>
              <Row><F label="Slug (unique)" v={d.slug} onC={(v) => set("slug", v)} disabled={!editing.isNew} testid="f-slug" /><F label="Read minutes" type="number" v={d.read_minutes} onC={(v) => set("read_minutes", v)} /></Row>
              <F label="Title" v={d.title} onC={(v) => set("title", v)} />
              <TA label="Excerpt" v={d.excerpt} onC={(v) => set("excerpt", v)} />
              <F label="Cover image URL" v={d.cover_image} onC={(v) => set("cover_image", v)} />
              <Row><F label="Author" v={d.author} onC={(v) => set("author", v)} /><F label="Tags (comma)" v={arr(d.tags, ", ")} onC={(v) => set("tags", v)} /></Row>
              <Row><F label="Published date" v={d.published_date} onC={(v) => set("published_date", v)} /><F label="Updated date" v={d.updated_date} onC={(v) => set("updated_date", v)} /></Row>
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={d.risk_disclaimer} onChange={(e) => set("risk_disclaimer", e.target.checked)} /> Show risk/advisory disclaimer</label>
              <TA label="Table of contents (one per line)" v={arr(d.toc)} onC={(v) => set("toc", v)} />
              <TA label="Body (use ## for headings, blank line between paragraphs)" rows={10} v={d.body} onC={(v) => set("body", v)} />
              <F label="Related device slugs (comma)" v={arr(d.related_devices, ", ")} onC={(v) => set("related_devices", v)} />
              <F label="Related category slugs (comma)" v={arr(d.related_categories, ", ")} onC={(v) => set("related_categories", v)} />
              <p className="text-xs text-slate-500">Note: FAQs are preserved from seed on edit; add FAQ editing later if needed.</p>
            </>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg border border-border bg-graphite px-4 py-2 text-sm text-slate-300">Cancel</button>
          <button data-testid="save-entity" onClick={save} disabled={saving} className="rounded-lg bg-neon px-5 py-2 text-sm font-heading font-bold text-charcoal disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

const arr = (v, sep = "\n") => (Array.isArray(v) ? v.join(sep) : v || "");
const Row = ({ children }) => <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
const F = ({ label, v, onC, type = "text", disabled, testid }) => (
  <div>
    <label className="text-xs text-slate-400">{label}</label>
    <input data-testid={testid} type={type} value={v} disabled={disabled} onChange={(e) => onC(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-graphite px-3 py-2 text-sm text-white focus:border-neon/50 focus:outline-none disabled:opacity-50" />
  </div>
);
const TA = ({ label, v, onC, rows = 3 }) => (
  <div>
    <label className="text-xs text-slate-400">{label}</label>
    <textarea rows={rows} value={v} onChange={(e) => onC(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-graphite px-3 py-2 text-sm text-white focus:border-neon/50 focus:outline-none" />
  </div>
);
const Sel = ({ label, v, onC, opts }) => (
  <div>
    <label className="text-xs text-slate-400">{label}</label>
    <select value={v} onChange={(e) => onC(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-graphite px-3 py-2 text-sm text-white focus:border-neon/50 focus:outline-none">
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);
